import {Link as RouterLink} from '@github-ui/react-router'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {testIdProps} from '@github-ui/test-id-props'
import type {LinkProps as PrimerLinkProps} from '@primer/react'
import {CounterLabel, Text} from '@primer/react'
import {clsx} from 'clsx'
import type {ComponentProps, ReactElement, ReactNode} from 'react'

import type {StylableProps} from '../types'
import styles from './SectionFilterLink.module.css'

export type ListViewSectionFilterLinks = Array<ReactElement<typeof ListViewSectionFilterLink>>

type SharedSectionFilterLinkProps = Omit<PrimerLinkProps, 'as' | 'href' | 'sx'> &
  StylableProps & {
    /**
     * The name of this section filter, e.g., "Open" to describe a section filter that shows only open issues.
     */
    title: string
    /**
     * The number of list items in the category this section filter represents. Optional. Changes appearance when
     * `isLoading` is true.
     */
    count?: string | number | ReactNode
    /** Optionally specify whether this section filter is currently active. Defaults to not being selected. */
    isSelected?: boolean
    /**
     * Optionally specify whether the section filter is still being loaded. Will be used to style the count, if given,
     * when true.
     */
    isLoading?: boolean
  }

type AnchorSectionFilterLinkProps = {
  /** The URL to load with a standard anchor navigation. */
  href: string
  to?: never
}

type RouterSectionFilterLinkProps = Omit<
  ComponentProps<typeof RouterLink>,
  keyof SharedSectionFilterLinkProps | 'children' | 'href' | 'to'
> & {
  href?: never
  /** The destination to load with client-side router navigation. */
  to: ComponentProps<typeof RouterLink>['to']
}

export type ListViewSectionFilterLinkProps = SharedSectionFilterLinkProps &
  (AnchorSectionFilterLinkProps | RouterSectionFilterLinkProps)

/**
 * Represents a single categorical section filter. Used to allow a user to filter the ListView's list items to only
 * show those matching this filter.
 */
export const ListViewSectionFilterLink = ({
  title,
  count,
  isSelected = false,
  isLoading = false,
  className,
  ...rest
}: ListViewSectionFilterLinkProps) => {
  const content = (
    <>
      <div className={styles.title}>{title}</div>
      {count !== undefined && (
        <CounterLabel className={clsx(styles.count, isLoading && styles.loading)}>{count}</CounterLabel>
      )}
      {count === undefined && isLoading && (
        <LoadingSkeleton variant="pill" height="18px" width="32px" className={styles.counterLabelSkeleton} />
      )}
    </>
  )
  const sharedProps = {
    className: clsx(styles.container, isSelected && styles.selected, className),
    ...testIdProps('list-view-section-filter-link'),
    'aria-current': isSelected ? ('true' as const) : undefined,
  }

  return rest.to !== undefined ? (
    <Text as={RouterLink} {...sharedProps} {...rest}>
      {content}
    </Text>
  ) : (
    <Text as="a" {...sharedProps} {...rest}>
      {content}
    </Text>
  )
}
